// src/components/BulkDishCreator.js
import React, { useState, useEffect } from 'react';
import './BulkDishCreator.css';

const BulkDishCreator = () => {
  const [cities, setCities] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [selectedCity, setSelectedCity] = useState('toronto');
  const [selectedChef, setSelectedChef] = useState('');
  const [dishTemplates, setDishTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customizations, setCustomizations] = useState({});
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState([]);

  // Pre-defined gourmet dish templates
  const GOURMET_TEMPLATES = [
    {
      id: 'lobster-thermidor',
      name: 'Lobster Thermidor',
      description: 'Classic French dish with fresh Maine lobster, cream sauce, and brandy',
      price: 45.00,
      category: 'main-course',
      prep_time: '45 minutes',
      servings: 2,
      difficulty: 'hard',
      dietary: ['dairy-free'],
      ingredients: 'lobster, cream, brandy, herbs, cheese',
      allergens: 'shellfish, dairy',
      image_urls: [
        'https://picsum.photos/800/600?random=1&text=Lobster+Thermidor',
        'https://picsum.photos/800/600?random=2&text=Lobster+Detail'
      ]
    },
    {
      id: 'beef-wellington',
      name: 'Beef Wellington',
      description: 'Tender beef fillet wrapped in puff pastry with mushroom duxelles',
      price: 65.00,
      category: 'main-course',
      prep_time: '3 hours',
      servings: 4,
      difficulty: 'expert',
      dietary: [],
      ingredients: 'beef fillet, puff pastry, mushrooms, prosciutto, herbs',
      allergens: 'gluten, dairy',
      image_urls: [
        'https://picsum.photos/800/600?random=3&text=Beef+Wellington',
        'https://picsum.photos/800/600?random=4&text=Wellington+Cross'
      ]
    },
    {
      id: 'brisket-tacos',
      name: 'Texas Brisket Tacos',
      description: 'Slow-smoked brisket with house-made tortillas and fresh salsa',
      price: 18.00,
      category: 'main-course',
      prep_time: '8 hours',
      servings: 3,
      difficulty: 'medium',
      dietary: ['gluten-free'],
      ingredients: 'brisket, corn tortillas, salsa, cilantro, lime',
      allergens: 'none',
      image_urls: [
        'https://picsum.photos/800/600?random=5&text=Brisket+Tacos',
        'https://picsum.photos/800/600?random=6&text=Taco+Close'
      ]
    },
    {
      id: 'smoked-ribs',
      name: 'Smoked Ribs',
      description: 'Fall-off-the-bone ribs with house BBQ sauce',
      price: 28.00,
      category: 'main-course',
      prep_time: '6 hours',
      servings: 2,
      difficulty: 'medium',
      dietary: [],
      ingredients: 'pork ribs, BBQ sauce, spices, brown sugar',
      allergens: 'none',
      image_urls: [
        'https://picsum.photos/800/600?random=7&text=Smoked+Ribs',
        'https://picsum.photos/800/600?random=8&text=Ribs+Sauce'
      ]
    },
    {
      id: 'clam-chowder',
      name: 'New England Clam Chowder',
      description: 'Creamy chowder with fresh clams, potatoes, and bacon',
      price: 16.00,
      category: 'appetizer',
      prep_time: '30 minutes',
      servings: 4,
      difficulty: 'easy',
      dietary: ['dairy-free'],
      ingredients: 'clams, potatoes, cream, bacon, onions',
      allergens: 'shellfish, dairy',
      image_urls: [
        'https://picsum.photos/800/600?random=9&text=Clam+Chowder',
        'https://picsum.photos/800/600?random=10&text=Chowder+Bowl'
      ]
    },
    {
      id: 'lava-cake',
      name: 'Chocolate Lava Cake',
      description: 'Warm chocolate cake with molten center and vanilla ice cream',
      price: 12.00,
      category: 'dessert',
      prep_time: '20 minutes',
      servings: 1,
      difficulty: 'medium',
      dietary: ['vegetarian'],
      ingredients: 'dark chocolate, butter, eggs, sugar, flour',
      allergens: 'eggs, dairy, gluten',
      image_urls: [
        'https://picsum.photos/800/600?random=11&text=Lava+Cake',
        'https://picsum.photos/800/600?random=12&text=Molten+Center'
      ]
    },
    {
      id: 'ramen-bowl',
      name: 'Tonkotsu Ramen',
      description: 'Rich pork bone broth with chashu pork, soft-boiled egg, and fresh noodles',
      price: 18.00,
      category: 'main-course',
      prep_time: '45 minutes',
      servings: 1,
      difficulty: 'hard',
      dietary: [],
      ingredients: 'pork bones, ramen noodles, chashu pork, egg, nori',
      allergens: 'gluten, eggs, soy',
      image_urls: [
        'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=14&text=Ramen+Detail'
      ]
    },
    {
      id: 'pad-thai',
      name: 'Pad Thai',
      description: 'Stir-fried rice noodles with shrimp, tofu, bean sprouts, and crushed peanuts',
      price: 16.00,
      category: 'main-course',
      prep_time: '25 minutes',
      servings: 2,
      difficulty: 'medium',
      dietary: [],
      ingredients: 'rice noodles, shrimp, tofu, bean sprouts, peanuts, tamarind',
      allergens: 'shellfish, peanuts, soy',
      image_urls: [
        'https://images.unsplash.com/photo-1559181567-c3190ca9959b?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=16&text=Pad+Thai+Close'
      ]
    },
    {
      id: 'carbonara',
      name: 'Spaghetti Carbonara',
      description: 'Classic Roman pasta with eggs, pecorino cheese, pancetta, and black pepper',
      price: 22.00,
      category: 'main-course',
      prep_time: '20 minutes',
      servings: 2,
      difficulty: 'medium',
      dietary: ['vegetarian'],
      ingredients: 'spaghetti, eggs, pecorino cheese, pancetta, black pepper',
      allergens: 'gluten, eggs, dairy',
      image_urls: [
        'https://images.unsplash.com/photo-1621996346565-e3dbc353d946?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=18&text=Carbonara+Detail'
      ]
    },
    {
      id: 'margherita-pizza',
      name: 'Margherita Pizza',
      description: 'Neapolitan-style pizza with San Marzano tomatoes, fresh mozzarella, and basil',
      price: 24.00,
      category: 'main-course',
      prep_time: '15 minutes',
      servings: 2,
      difficulty: 'easy',
      dietary: ['vegetarian'],
      ingredients: 'pizza dough, San Marzano tomatoes, mozzarella, basil, olive oil',
      allergens: 'gluten, dairy',
      image_urls: [
        'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=20&text=Pizza+Slice'
      ]
    },
    {
      id: 'carnitas-tacos',
      name: 'Carnitas Tacos',
      description: 'Slow-cooked pork carnitas with onions, cilantro, and lime on corn tortillas',
      price: 14.00,
      category: 'main-course',
      prep_time: '4 hours',
      servings: 3,
      difficulty: 'medium',
      dietary: [],
      ingredients: 'pork shoulder, corn tortillas, onions, cilantro, lime',
      allergens: 'none',
      image_urls: [
        'https://images.unsplash.com/photo-1565299585323-38174c4aab98?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=22&text=Taco+Close'
      ]
    },
    {
      id: 'chicken-enchiladas',
      name: 'Chicken Enchiladas',
      description: 'Rolled tortillas filled with shredded chicken, covered in red sauce and cheese',
      price: 17.00,
      category: 'main-course',
      prep_time: '35 minutes',
      servings: 3,
      difficulty: 'medium',
      dietary: [],
      ingredients: 'chicken, tortillas, red sauce, cheese, onions',
      allergens: 'gluten, dairy',
      image_urls: [
        'https://images.unsplash.com/photo-1565299585323-38174c4aab98?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=24&text=Enchiladas+Close'
      ]
    },
    {
      id: 'tiramisu',
      name: 'Tiramisu',
      description: 'Classic Italian dessert with coffee-soaked ladyfingers and mascarpone cream',
      price: 13.00,
      category: 'dessert',
      prep_time: '30 minutes',
      servings: 4,
      difficulty: 'medium',
      dietary: ['vegetarian'],
      ingredients: 'ladyfingers, mascarpone, coffee, eggs, sugar, cocoa',
      allergens: 'gluten, eggs, dairy',
      image_urls: [
        'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=26&text=Tiramisu+Slice'
      ]
    },
    {
      id: 'fish-chips',
      name: 'Fish & Chips',
      description: 'Beer-battered cod with hand-cut fries, mushy peas, and tartar sauce',
      price: 19.00,
      category: 'main-course',
      prep_time: '30 minutes',
      servings: 2,
      difficulty: 'medium',
      dietary: [],
      ingredients: 'cod fillet, beer batter, potatoes, peas, tartar sauce',
      allergens: 'gluten, fish',
      image_urls: [
        'https://images.unsplash.com/photo-1544982503-9f984c14501a?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=28&text=Fish+Chips+Close'
      ]
    },
    {
      id: 'deep-dish-pizza',
      name: 'Chicago Deep Dish',
      description: 'Thick-crust pizza with chunky tomato sauce and layers of mozzarella cheese',
      price: 28.00,
      category: 'main-course',
      prep_time: '45 minutes',
      servings: 4,
      difficulty: 'medium',
      dietary: ['vegetarian'],
      ingredients: 'pizza dough, chunky tomatoes, mozzarella, basil, olive oil',
      allergens: 'gluten, dairy',
      image_urls: [
        'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop',
        'https://picsum.photos/800/600?random=30&text=Deep+Dish+Slice'
      ]
    }
  ];

  // Use the same cities as ChefWidget
  const CITIES = [
    { id: 'toronto', name: 'Toronto' },
    { id: 'new-york', name: 'New York' },
    { id: 'boston', name: 'Boston' },
    { id: 'chicago', name: 'Chicago' },
    { id: 'new-orleans', name: 'New Orleans' },
    { id: 'miami', name: 'Miami' },
    { id: 'austin', name: 'Austin' },
    { id: 'dallas', name: 'Dallas' },
    { id: 'houston', name: 'Houston' }
  ];

  useEffect(() => {
    console.log('BulkDishCreator: Setting dish templates:', GOURMET_TEMPLATES.length);
    setDishTemplates(GOURMET_TEMPLATES);
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadChefsForCity(selectedCity);
    } else {
      setChefs([]);
      setSelectedChef('');
    }
  }, [selectedCity]);

  const loadChefsForCity = async (city) => {
    try {
      const response = await fetch(`/api/chefs?city=${city}&status=approved,live`);
      const data = await response.json();
      setChefs(data.results || []);
    } catch (error) {
      console.error('Error loading chefs:', error);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = GOURMET_TEMPLATES.find(t => t.id === templateId);
    setSelectedTemplate(templateId);
    setCustomizations({
      name: template.name,
      description: template.description,
      price: template.price,
      category: template.category,
      prep_time: template.prep_time,
      servings: template.servings,
      difficulty: template.difficulty,
      dietary: template.dietary,
      ingredients: template.ingredients,
      allergens: template.allergens
    });
  };

  const createDishFromTemplate = async () => {
    if (!selectedChef || !selectedTemplate) {
      alert('Please select a chef and dish template');
      return;
    }

    setCreating(true);
    
    try {
      const template = GOURMET_TEMPLATES.find(t => t.id === selectedTemplate);
      const chef = chefs.find(c => c.name === selectedChef);
      
      if (!chef) {
        throw new Error('Chef not found');
      }

      // Use image URLs directly (no need to re-upload)
      const imageResults = [];
      for (let i = 0; i < template.image_urls.length; i++) {
        const imageUrl = template.image_urls[i];
        const filename = `${customizations.name.replace(/\s+/g, '_')}_${i + 1}.jpg`;
        
        // Use the image URL directly instead of re-uploading
        imageResults.push({
          id: `direct-${Date.now()}-${i}`,
          url: imageUrl,
          filename: filename
        });
      }

      // Create dish record
      const dishRecord = {
        chef_id: chef.id, // Use the chef ID as-is (could be string UUID or number)
        name: customizations.name,
        description: customizations.description,
        price_cents: Math.round(customizations.price * 100),
        currency: 'USD',
        categories: customizations.category,
        tags: JSON.stringify({
          prep_time: customizations.prep_time,
          servings: customizations.servings,
          difficulty: customizations.difficulty,
          dietary: customizations.dietary,
          ingredients: customizations.ingredients,
          allergens: customizations.allergens,
          city: selectedCity,
          chef_name: selectedChef,
          images: imageResults
        }),
        active: 1,
        created_at: new Date().toISOString()
      };

      // Save to database
      const response = await fetch('/api/admin/dishes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'Boodgy'}`
        },
        body: JSON.stringify(dishRecord)
      });

      if (!response.ok) {
        throw new Error(`Failed to save dish: ${response.status}`);
      }

      const result = await response.json();
      
      setResults(prev => [...prev, {
        dish_id: result.dish_id,
        dish_name: customizations.name,
        chef_name: selectedChef,
        city: selectedCity,
        images_uploaded: imageResults.length
      }]);

      alert(`Successfully created "${customizations.name}" for ${selectedChef} with ${imageResults.length} images!`);
      
      // Reset form
      setSelectedTemplate('');
      setCustomizations({});
      
    } catch (error) {
      console.error('Error creating dish:', error);
      alert(`Error creating dish: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const uploadImageToCloudflare = async (imageUrl, filename, metadata) => {
    // Get direct upload URL
    const uploadResponse = await fetch('/api/images/direct-upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'Boodgy'}`
      },
      body: JSON.stringify({
        metadata: {
          type: 'dishes',
          category: 'gourmet-dishes',
          folder: 'dishes',
          fileName: filename,
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'bulk-dish-creator',
          purpose: 'dish-photo',
          chefName: metadata.chef_name,
          city: metadata.city,
          dishName: metadata.dish_name,
          dishCategory: metadata.category
        }
      })
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to get upload URL: ${uploadResponse.status}`);
    }

    const { uploadURL } = await uploadResponse.json();

    // Download and upload image
    const imageResponse = await fetch(imageUrl);
    const imageBlob = await imageResponse.blob();

    const formData = new FormData();
    formData.append('file', imageBlob, filename);

    const uploadResult = await fetch(uploadURL, {
      method: 'POST',
      body: formData
    });

    if (!uploadResult.ok) {
      throw new Error(`Upload failed: ${uploadResult.status}`);
    }

    const result = await uploadResult.json();
    console.log('BulkDishCreator upload result:', result);
    
    // Cloudflare Images returns { id: "image-id", filename: "filename.jpg" }
    if (result.id) {
      return {
        id: result.id,
        filename: result.filename || filename,
        url: `https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/${result.id}/public`
      };
    } else {
      throw new Error('Upload response missing image ID');
    }
  };

  return (
    <div className="bulk-dish-creator">
      <div className="creator-header">
        <h2>Bulk Dish Creator</h2>
        <p>Create gourmet dishes with images and metadata using pre-defined templates</p>
      </div>

      {/* City and Chef Selection */}
      <div className="selection-section">
        <div className="city-pills">
          {CITIES.map(city => (
            <button
              key={city.id}
              className={`city-pill ${selectedCity === city.id ? 'active' : ''}`}
              onClick={() => setSelectedCity(city.id)}
            >
              {city.name}
            </button>
          ))}
        </div>

        {selectedCity && (
          <div className="chef-selection">
            <h4>Select Chef</h4>
            <div className="chef-pills">
              {chefs.map(chef => (
                <button
                  key={chef.id}
                  className={`chef-pill ${selectedChef === chef.name ? 'active' : ''}`}
                  onClick={() => setSelectedChef(chef.name)}
                >
                  {chef.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Dish Template Selection */}
      {selectedChef && (
        <div className="template-section">
          <h3>Select Dish Template</h3>
          <div className="template-grid">
            {console.log('BulkDishCreator: Rendering templates:', dishTemplates.length)}
            {dishTemplates.map(template => (
              <div 
                key={template.id} 
                className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                onClick={() => handleTemplateSelect(template.id)}
              >
                <div className="template-preview">
                  <img src={template.image_urls[0]} alt={template.name} />
                </div>
                <div className="template-info">
                  <h4>{template.name}</h4>
                  <p className="template-price">${template.price}</p>
                  <p className="template-category">{template.category}</p>
                  <p className="template-description">{template.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customization Form */}
      {selectedTemplate && (
        <div className="customization-section">
          <h3>Customize Dish Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Dish Name</label>
              <input
                type="text"
                value={customizations.name || ''}
                onChange={(e) => setCustomizations(prev => ({ ...prev, name: e.target.value }))}
                className="dish-name-input"
              />
            </div>
            <div className="form-group">
              <label>Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={customizations.price || ''}
                onChange={(e) => setCustomizations(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                className="price-input"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={customizations.description || ''}
              onChange={(e) => setCustomizations(prev => ({ ...prev, description: e.target.value }))}
              className="description-textarea"
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Prep Time</label>
              <input
                type="text"
                value={customizations.prep_time || ''}
                onChange={(e) => setCustomizations(prev => ({ ...prev, prep_time: e.target.value }))}
                className="prep-time-input"
              />
            </div>
            <div className="form-group">
              <label>Servings</label>
              <input
                type="number"
                value={customizations.servings || ''}
                onChange={(e) => setCustomizations(prev => ({ ...prev, servings: parseInt(e.target.value) }))}
                className="servings-input"
              />
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select
                value={customizations.difficulty || 'medium'}
                onChange={(e) => setCustomizations(prev => ({ ...prev, difficulty: e.target.value }))}
                className="difficulty-select"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Key Ingredients</label>
              <input
                type="text"
                value={customizations.ingredients || ''}
                onChange={(e) => setCustomizations(prev => ({ ...prev, ingredients: e.target.value }))}
                className="ingredients-input"
              />
            </div>
            <div className="form-group">
              <label>Allergens</label>
              <input
                type="text"
                value={customizations.allergens || ''}
                onChange={(e) => setCustomizations(prev => ({ ...prev, allergens: e.target.value }))}
                className="allergens-input"
              />
            </div>
          </div>

          <div className="create-controls">
            <button 
              onClick={createDishFromTemplate}
              disabled={creating || !customizations.name}
              className="create-dish-btn"
            >
              {creating ? 'Creating Dish...' : `Create "${customizations.name}" for ${selectedChef}`}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="results-section">
          <h3>Created Dishes</h3>
          <div className="results-list">
            {results.map((result, index) => (
              <div key={index} className="result-item">
                <span className="result-name">{result.dish_name}</span>
                <span className="result-chef">{result.chef_name}</span>
                <span className="result-city">{result.city}</span>
                <span className="result-images">{result.images_uploaded} images</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkDishCreator;
